// FILE: src/components/ManageUsers.js
import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Avatar,
  IconButton,
  Fade,
  Divider,
  Container,
  Card,
  CardContent,
  Tooltip,
  TableContainer,
  alpha,
  useTheme
} from '@mui/material';
import {
  Save as SaveIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  MarkChatRead as MarkChatReadIcon // Icon for the new button
} from '@mui/icons-material';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, writeBatch, query, where } from 'firebase/firestore'; // Import firestore functions
import { AuthContext } from '../contexts/AuthContext';

const ManageUsers = () => {
  const { currentUser } = useContext(AuthContext);
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [savingUserId, setSavingUserId] = useState(null);
  const [isMarkingAsRead, setIsMarkingAsRead] = useState(false); // State for the new action

  // State to hold the selections for each user being edited
  const [editedClientIds, setEditedClientIds] = useState({});
  const [hasChanges, setHasChanges] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setErrorMsg('');
      try {
        // Fetch all clients
        const clientsSnapshot = await getDocs(collection(db, 'clients'));
        const clientsList = clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setClients(clientsList);

        // Fetch all users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(usersList);

        // Initialize the editedClientIds state
        const initialEdits = {};
        const initialChanges = {};
        usersList.forEach(user => {
          initialEdits[user.id] = user.clientIds || [];
          initialChanges[user.id] = false;
        });
        setEditedClientIds(initialEdits);
        setHasChanges(initialChanges);

      } catch (error) {
        console.error('Error fetching data:', error);
        setErrorMsg('Failed to load user or client data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleClientSelectionChange = (event, userId) => {
    const { target: { value } } = event;
    setEditedClientIds(prev => ({
      ...prev,
      [userId]: typeof value === 'string' ? value.split(',') : value,
    }));
    
    // Check if there are changes
    const user = users.find(u => u.id === userId);
    const originalClientIds = user?.clientIds || [];
    const newClientIds = typeof value === 'string' ? value.split(',') : value;
    const hasChange = JSON.stringify(originalClientIds.sort()) !== JSON.stringify(newClientIds.sort());
    
    setHasChanges(prev => ({
      ...prev,
      [userId]: hasChange
    }));
  };

  const handleSaveChanges = async (userId) => {
    setErrorMsg('');
    setSuccessMsg('');
    setSavingUserId(userId);
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        clientIds: editedClientIds[userId]
      });
      
      // Update the user in the local state
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, clientIds: editedClientIds[userId] } : user
      ));
      
      setHasChanges(prev => ({
        ...prev,
        [userId]: false
      }));
      
      const userEmail = users.find(u => u.id === userId)?.email || 'User';
      setSuccessMsg(`Successfully updated clients for ${userEmail}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (error) {
      console.error('Error updating user:', error);
      setErrorMsg(`Failed to update clients for user.`);
    } finally {
      setSavingUserId(null);
    }
  };

  /**
   * Finds all unread 'OAfeedback' documents and marks them as read.
   */
  const handleMarkAllOARead = async () => {
    setIsMarkingAsRead(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const feedbackRef = collection(db, 'feedback');
      const q = query(feedbackRef, where('assignedTo', '==', 'OAfeedback'), where('isRead', '==', false));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setSuccessMsg("No unread OAfeedback to mark as read.");
        setTimeout(() => setSuccessMsg(''), 4000);
        setIsMarkingAsRead(false);
        return;
      }
      
      const batch = writeBatch(db);
      querySnapshot.forEach((docSnapshot) => {
        const docRef = doc(db, 'feedback', docSnapshot.id);
        batch.update(docRef, { isRead: true });
      });
      
      await batch.commit();
      setSuccessMsg(`Successfully marked ${querySnapshot.size} OAfeedback items as read.`);
      setTimeout(() => setSuccessMsg(''), 4000);

    } catch (error) {
      console.error('Error marking OAfeedback as read:', error);
      setErrorMsg('An error occurred. Could not mark all OAfeedback items as read.');
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PeopleIcon sx={{ 
            fontSize: 40, 
            mr: 2, 
            color: theme.palette.primary.main 
          }} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 600, 
              color: theme.palette.text.primary,
              fontFamily: 'Inter, Montserrat, sans-serif'
            }}
          >
            Manage User Clients
          </Typography>
        </Box>
        <Typography 
          variant="body1" 
          sx={{ 
            color: theme.palette.text.secondary,
            mb: 3
          }}
        >
          Assign and manage client access for each user in your organization.
        </Typography>

        {/* Action Button to mark all OAfeedback as read */}
        <Box sx={{ mt: 3, mb: 3 }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleMarkAllOARead}
            disabled={isMarkingAsRead}
            startIcon={
              isMarkingAsRead ? <CircularProgress size={20} color="inherit" /> : <MarkChatReadIcon />
            }
            sx={{
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {isMarkingAsRead ? 'Marking as Read...' : 'Mark All OAfeedback as Read'}
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: theme.palette.text.secondary }}>
            This action will mark all notifications from anonymous feedback sources as read for all users.
          </Typography>
        </Box>
        <Divider />
      </Box>

      {/* Alert Messages */}
      <Fade in={!!errorMsg}>
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          icon={<ErrorIcon />}
          onClose={() => setErrorMsg('')}
        >
          {errorMsg}
        </Alert>
      </Fade>
      
      <Fade in={!!successMsg}>
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          icon={<CheckCircleIcon />}
          onClose={() => setSuccessMsg('')}
        >
          {successMsg}
        </Alert>
      </Fade>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Card sx={{ 
          flex: 1, 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {users.length}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Total Users
                </Typography>
              </Box>
              <PersonIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ 
          flex: 1,
          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: 'white'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {clients.length}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Total Clients
                </Typography>
              </Box>
              <BusinessIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Main Table */}
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden'
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                '& th': {
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: theme.palette.text.secondary,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  py: 2
                }
              }}>
                <TableCell>USER</TableCell>
                <TableCell>ASSIGNED CLIENTS</TableCell>
                <TableCell align="center">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user, index) => (
                <TableRow 
                  key={user.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.02)
                    },
                    '& td': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      py: 2.5
                    }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        sx={{ 
                          mr: 2,
                          bgcolor: theme.palette.primary.main,
                          width: 36,
                          height: 36
                        }}
                      >
                        {user.email?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {user.email}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          User ID: {user.id.slice(0, 8)}...
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <FormControl 
                      fullWidth 
                      variant="outlined"
                      size="small"
                      sx={{ 
                        maxWidth: 400,
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main,
                          }
                        }
                      }}
                    >
                      <Select
                        multiple
                        value={editedClientIds[user.id] || []}
                        onChange={(e) => handleClientSelectionChange(e, user.id)}
                        input={<OutlinedInput />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.length === 0 ? (
                              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                No clients assigned
                              </Typography>
                            ) : (
                              selected.map((value) => {
                                const client = clients.find(c => c.id === value);
                                return (
                                  <Chip 
                                    key={value} 
                                    label={client?.displayName || value}
                                    size="small"
                                    sx={{
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                      color: theme.palette.primary.main,
                                      '& .MuiChip-label': {
                                        px: 1.5
                                      }
                                    }}
                                  />
                                );
                              })
                            )}
                          </Box>
                        )}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300
                            }
                          }
                        }}
                      >
                        {clients.map((client) => (
                          <MenuItem key={client.id} value={client.id}>
                            <Checkbox 
                              checked={(editedClientIds[user.id] || []).indexOf(client.id) > -1}
                              sx={{
                                '&.Mui-checked': {
                                  color: theme.palette.primary.main
                                }
                              }}
                            />
                            <ListItemText 
                              primary={client.displayName || client.id}
                              secondary={`ID: ${client.id.slice(0, 8)}...`}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={hasChanges[user.id] ? "Save changes" : "No changes to save"}>
                      <span>
                        <Button
                          variant={hasChanges[user.id] ? "contained" : "outlined"}
                          size="small"
                          onClick={() => handleSaveChanges(user.id)}
                          disabled={!hasChanges[user.id] || savingUserId === user.id}
                          startIcon={
                            savingUserId === user.id ? 
                              <CircularProgress size={16} color="inherit" /> : 
                              <SaveIcon />
                          }
                          sx={{
                            minWidth: 100,
                            textTransform: 'none',
                            fontWeight: 500,
                            ...(hasChanges[user.id] && {
                              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                              boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
                            })
                          }}
                        >
                          {savingUserId === user.id ? 'Saving...' : 'Save'}
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default ManageUsers;